import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Reveal, SectionTag, SectionHeading } from "@/components/Reveal";

export const CoverageSection = () => {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => ["All", ...new Set(articles.map((a) => a.category))],
    [articles]
  );
  const visible = filter === "All" ? articles : articles.filter((a) => a.category === filter);

  return (
    <section id="coverage" data-testid="coverage-section" className="px-4 sm:px-8 lg:px-16 py-16 lg:py-28">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <SectionHeading>
            Sports <span className="text-[var(--ekal-accent)]">Coverage</span>
          </SectionHeading>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
            {visible.length} dispatches
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`coverage-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`text-[11px] font-mono uppercase tracking-[0.18em] px-4 py-2 border transition-colors ${
                filter === c
                  ? "border-[var(--ekal-accent)] bg-[var(--ekal-accent)] text-white"
                  : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </Reveal>

      {loading ? (
        <div className="py-20" data-testid="coverage-loading" aria-hidden="true" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[300px] gap-3">
          {visible.map((a, i) => (
            <Reveal
              key={a.id}
              delay={Math.min(i * 0.06, 0.3)}
              className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}
            >
              <Link
                to={`/article/${a.id}`}
                data-testid={`article-card-${a.id}`}
                className="group relative block h-full w-full overflow-hidden border border-white/10 hover:border-[var(--ekal-accent)]/60 transition-colors bg-[#181C26]"
              >
                <img
                  src={a.cover_url}
                  alt={a.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ekal-bg)] via-[var(--ekal-bg)]/35 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3">
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400 border border-amber-400/40 px-2.5 py-1 whitespace-nowrap">
                      {a.category}
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 whitespace-nowrap">
                      {fmtDate(a.created_at)} · {a.read_time}
                    </span>
                  </div>
                  <h3
                    className={`font-heading font-bold uppercase tracking-tight leading-none text-slate-50 group-hover:text-white ${
                      i === 0 ? "text-2xl sm:text-4xl" : "text-xl sm:text-2xl"
                    }`}
                  >
                    {a.title}
                  </h3>
                  {i === 0 && (
                    <p className="mt-3 max-w-xl text-sm text-slate-300 leading-relaxed hidden sm:block">
                      {a.excerpt}
                    </p>
                  )}
                </div>
              </Link>
            </Reveal>
          ))}
          {visible.length === 0 && (
            <p className="text-sm text-slate-500 font-mono uppercase tracking-[0.2em] py-16 col-span-full text-center">
              No dispatches filed in this category yet.
            </p>
          )}
        </div>
      )}
    </section>
  );
};
